import { FormState } from 'react-hook-form';
import get from 'lodash/get';
import { identity } from 'fp-ts/function';

import {
  CAPPING_MAX_ALLOWED,
  CAPPING_PERIOD,
  OPTIMIZE_MAX_ALLOWED,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_PERIOD,
  FrequencyManagementSchema,
  MESSAGE_TYPES,
  MODE,
  MessageTypeSchema,
  SchemaFormKeys,
} from 'modules/frequencyManagement/contexts/FormProvider';
import { FrequencyMode, FrequencyPeriod } from 'modules/frequencyManagement/lib/types';
import { doesExist } from 'utils/tsHelpers';

export const deriveDirtyState = (
  messageMediumMode: FrequencyMode,
  allDirtyFields: FormState<FrequencyManagementSchema>['dirtyFields'] | undefined,
  messageTypes: MessageTypeSchema[] | undefined,
  includeMode: boolean,
) => {
  let mediumIsDirty = false;
  const propertiesByMode: Record<FrequencyMode, SchemaFormKeys[]> = {
    [FrequencyMode.Off]: [],
    [FrequencyMode.NoCap]: [],
    [FrequencyMode.Cap]: includeMode
      ? [CAPPING_MAX_ALLOWED, CAPPING_PERIOD, MODE]
      : [CAPPING_MAX_ALLOWED, CAPPING_PERIOD],
    [FrequencyMode.Optimize]: includeMode
      ? [OPTIMIZE_MAX_ALLOWED, OPTIMIZE_MIN_ALLOWED, OPTIMIZE_PERIOD, MODE]
      : [OPTIMIZE_MAX_ALLOWED, OPTIMIZE_MIN_ALLOWED, OPTIMIZE_PERIOD],
  };

  const mediumProperties = propertiesByMode[messageMediumMode];
  if (mediumProperties.length === 0) {
    mediumIsDirty = get(allDirtyFields, MODE, false);
  } else {
    mediumIsDirty = mediumProperties
      .map((property) => {
        return get(allDirtyFields, property, false);
      })
      .some(Boolean);
  }

  const messageTypesResult =
    messageTypes?.flatMap((messageType, index) => {
      if (!messageType || !messageType[MODE]) return false;

      const messageTypeMode = messageType[MODE];
      const messageTypeProperties = propertiesByMode[messageTypeMode];
      if (messageTypeProperties.length === 0) {
        const key = `${MESSAGE_TYPES}.${index}.${MODE}`;
        return get(allDirtyFields, key, false);
      } else {
        return messageTypeProperties.map((property) => {
          const key = `${MESSAGE_TYPES}.${index}.${property}`;
          return get(allDirtyFields, key, false);
        });
      }
    }) || [];
  const messageTypesDirty = messageTypesResult.some(Boolean);

  return mediumIsDirty || messageTypesDirty;
};

export type DataExportTransformer =
  | ((source: string | null) => string)
  | ((source: number | null) => number | string)
  | ((source: string | number) => number | string);
export const displayStringWithDefault = (source: string | null) => source || 'N/A';
export const displayNumberWithDefault = (source: string | null) =>
  Number.isNaN(source) || !doesExist(source) ? 'N/A' : source;
export const optimizeCSVDataHeadersMapping: Array<{
  label: string;
  key: string;
  exportName: string;
  transform: DataExportTransformer;
}> = [
  {
    label: 'Optimization ID',
    key: 'Optimization ID',
    exportName: 'optimization_id',
    transform: displayNumberWithDefault,
  },
  {
    label: 'Status',
    key: 'Status',
    exportName: 'status',
    transform: identity,
  },
  {
    label: 'Optimization Type',
    key: 'Optimization Type',
    exportName: 'optimization_type',
    transform: identity,
  },
  {
    label: 'Channel',
    key: 'Channel',
    exportName: 'channel',
    transform: identity,
  },
  {
    label: 'Message Type',
    key: 'Message Type',
    exportName: 'message_type',
    transform: displayStringWithDefault,
  },
  {
    label: 'Message Type ID',
    key: 'Message Type ID',
    exportName: 'message_type_id',
    transform: displayStringWithDefault,
  },
  {
    label: 'Optimization Range Min',
    key: 'Optimization Range Min',
    exportName: 'min_allowed',
    transform: identity,
  },
  {
    label: 'Optimization Range Max',
    key: 'Optimization Range Max',
    exportName: 'max_allowed',
    transform: identity,
  },
  {
    label: 'Duration',
    key: 'Duration',
    exportName: 'interval_days',
    transform: (daysCount: number | null) =>
      !daysCount || daysCount <= 7 ? FrequencyPeriod.Weekly : FrequencyPeriod.Monthly,
  },
  {
    label: 'Optimization Start Date',
    key: 'Optimization Start Date',
    exportName: 'start_date',
    transform: identity,
  },
  {
    label: 'Optimization End Date',
    key: 'Optimization End Date',
    exportName: 'end_date',
    transform: identity,
  },
  {
    label: 'Optimal Cap Found',
    key: 'Optimal Cap Found',
    exportName: 'optimal_cap',
    transform: (count: number | null) =>
      Number.isNaN(count) || !doesExist(count) || count < 0 ? 'holdout group' : count,
  },
  {
    label: 'User Count',
    key: 'User Count',
    exportName: 'user_count',
    transform: displayNumberWithDefault,
  },
  {
    label: 'Average Open Rate',
    key: 'Average Open Rate',
    exportName: 'engagement_rate',
    transform: displayNumberWithDefault,
  },
  {
    label: 'Average Unsubscribe Rate',
    key: 'Average Unsubscribe Rate',
    exportName: 'unsubscribe_rate',
    transform: displayNumberWithDefault,
  },
];

export const optimizeCSVDataHeaders = optimizeCSVDataHeadersMapping.map(
  ({ exportName, transform, ...rest }) => ({ ...rest }),
);
